/**
 * Reason codes the backend returns on the 404 body when an onboarding agreement PDF has no stored
 * copy. Mirrors DocumentUnavailableReason in the monorepo at
 * apps/common-public-service/src/modules/admin/tenant-docs/document-unavailable.ts.
 *
 * The backend also sends a display `message`; these codes exist so the UI can decide between an
 * informational and an error presentation without matching on message text.
 */
export const DOCUMENT_UNAVAILABLE_REASONS = ['TENANT_NOT_LINKED', 'DOCUMENT_NOT_YET_GENERATED'] as const

export type DocumentUnavailableReason = (typeof DOCUMENT_UNAVAILABLE_REASONS)[number]

export function isDocumentUnavailableReason(value: unknown): value is DocumentUnavailableReason {
  return typeof value === 'string' && DOCUMENT_UNAVAILABLE_REASONS.includes(value as DocumentUnavailableReason)
}

/** Shown in place of the view button, before any request is made. */
export const TENANT_NOT_CREATED_DOCUMENT_MESSAGE = 'Document viewable when tenant account is created'

function isExternalSignatureSource(signatureSource: string): boolean {
  return signatureSource === 'EXTERNAL' || signatureSource.startsWith('EXTERNAL|')
}

/**
 * Whether the admin UI should offer "View document" on an onboarding record.
 *
 * Resolution differs by agreement shape:
 * - **TESA click-to-accept** — only served from artifacts the tenant-docs-save worker writes under
 *   `tenants/{tenantCuid}/...`; hide until Create Tenant runs.
 * - **Utility Authorization (HelloSign PMA/CISR)** — viewer falls back to live HelloSign fetch via
 *   `signature_id`, so the button can show before tenant creation even though the save worker later
 *   copies a durable S3 object.
 * - **Externally uploaded** — served from the admin upload S3 key; viewable without a tenant.
 */
export function canShowOnboardingDocumentViewAction(opts: {
  hasLinkedTenant: boolean
  isAccountInviteCreated: boolean
  signatureSource?: string | null
  /** Pass true for rows in the TESA Click-To-Accept section only. */
  isTesaClickToAccept?: boolean
  /** Pass true for rows in the Externally Signed Documents section. */
  isExternallyStoredDocument?: boolean
}): boolean {
  if (opts.hasLinkedTenant || opts.isAccountInviteCreated) {
    return true
  }

  if (opts.isTesaClickToAccept) {
    return false
  }

  if (opts.isExternallyStoredDocument) {
    return true
  }

  if (opts.signatureSource === 'HelloSign') {
    return true
  }

  if (opts.signatureSource != null && isExternalSignatureSource(opts.signatureSource)) {
    return true
  }

  return false
}

/**
 * Whether viewing this agreement requires a linked tenant before the view action is offered.
 * Prefer {@link canShowOnboardingDocumentViewAction} for UI gating.
 */
export function requiresLinkedTenantForDocumentView(signatureSource: string | null | undefined): boolean {
  if (!signatureSource) {
    return true
  }
  if (isExternalSignatureSource(signatureSource)) {
    return false
  }
  if (signatureSource === 'HelloSign') {
    return false
  }
  return true
}
